import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CreditCard, ArrowUpRight, Loader2, Link2, Apple, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CASHAPP_URL = "https://cash.app/$etoira";
const CASHAPP_TAG = "$etoira";
const APPLEPAY_EMAIL = "adnl@dr.com";

const CHAIN_TIERS = [
  { q: 1, price: 30 },
  { q: 2, price: 50 },
  { q: 3, price: 80 },
  { q: 4, price: 100 },
];

const card = "border border-border bg-card p-6 flex flex-col hover:border-volt transition-colors duration-300";

export const Orders = () => {
  const [sel, setSel] = useState(null);
  const [customAmt, setCustomAmt] = useState("");

  const openCustom = () => {
    const amt = parseFloat(customAmt);
    if (!amt || amt < 1) { toast.error("Enter a custom amount (min $1)"); return; }
    setSel({ order_type: "custom", quantity: 1, amount: amt, label: "Custom Order", custom_amount: amt });
  };

  return (
    <section id="orders" className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="mb-14">
        <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">Commissions · Open</span>
        <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight uppercase mt-4">Personal orders</h2>
        <p className="text-muted-foreground max-w-xl mt-3 text-sm">Order custom UGC straight from the collective. Pick a package or request a custom quote — pay by card, CashApp or Apple Pay.</p>
      </div>

      {/* UGC CHAINS */}
      <div className="mb-10">
        <h3 className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mb-5">UGC Chains</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {CHAIN_TIERS.map((t) => (
            <div key={t.q} data-testid={`tier-chain-${t.q}`} className={card}>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-volt">{t.q} Chain{t.q > 1 ? "s" : ""}</span>
              <div className="font-display font-black text-4xl tracking-tighter mt-3">${t.price}</div>
              <button data-testid={`order-chain-${t.q}`} onClick={() => setSel({ order_type: "ugc_chains", quantity: t.q, amount: t.price, label: `${t.q} UGC Chain${t.q > 1 ? "s" : ""}` })}
                className="mt-6 bg-volt text-white font-mono text-[10px] tracking-[0.2em] uppercase py-3 hover:brightness-110 transition-all duration-200">Order</button>
            </div>
          ))}
        </div>
      </div>

      {/* ACCESSORIES + CUSTOM */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className={card}>
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-volt">UGC Accessories — hats & body</span>
          <div className="font-display font-black text-4xl tracking-tighter mt-3">$75</div>
          <p className="text-sm text-muted-foreground mt-2">Standard rate for any single hat or body accessory.</p>
          <button data-testid="order-accessory" onClick={() => setSel({ order_type: "ugc_accessory", quantity: 1, amount: 75, label: "UGC Accessory" })}
            className="mt-6 bg-volt text-white font-mono text-[10px] tracking-[0.2em] uppercase py-3 hover:brightness-110 transition-all duration-200">Order</button>
        </div>
        <div className={card}>
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-volt">Custom amount — bulk & complex</span>
          <p className="text-sm text-muted-foreground mt-2 mb-4">For bulk orders or complex detailed work, enter a custom amount you've agreed with the team.</p>
          <div className="flex gap-2 mt-auto">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <input data-testid="custom-amount-input" type="number" min="1" value={customAmt} onChange={(e) => setCustomAmt(e.target.value)}
                className="w-full bg-background border border-border focus:border-volt outline-none pl-7 pr-3 py-3 text-sm transition-colors duration-200" placeholder="Amount" />
            </div>
            <button data-testid="order-custom" onClick={openCustom} className="bg-volt text-white font-mono text-[10px] tracking-[0.2em] uppercase px-6 hover:brightness-110 transition-all duration-200">Order</button>
          </div>
        </div>
      </div>

      <CheckoutModal sel={sel} onClose={() => setSel(null)} />
    </section>
  );
};

function CheckoutModal({ sel, onClose }) {
  const [form, setForm] = useState({ customer_name: "", customer_contact: "", notes: "" });
  const [method, setMethod] = useState("stripe");
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = () => { setManual(null); setForm({ customer_name: "", customer_contact: "", notes: "" }); setMethod("stripe"); onClose(); };

  const submit = async () => {
    if (!form.customer_name.trim() || !form.customer_contact.trim()) { toast.error("Add your name and a contact (email or Discord)"); return; }
    setBusy(true);
    try {
      const body = { order_type: sel.order_type, quantity: sel.quantity, custom_amount: sel.custom_amount, ...form };
      if (method === "stripe") {
        const { data } = await api.post("/orders/checkout", { ...body, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      } else {
        const { data } = await api.post("/orders/manual", { ...body, method });
        setManual({ method, amount: data.amount });
        toast.success("Order placed — send payment to complete it");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not process your order");
    } finally { setBusy(false); }
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied"); };

  return (
    <AnimatePresence>
      {sel && (
        <motion.div data-testid="checkout-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close}
          className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-xl flex items-center justify-center p-4">
          <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }} transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.4 }}
            onClick={(e) => e.stopPropagation()} data-lenis-prevent className="relative w-full max-w-md bg-card border border-border p-7 max-h-[90vh] overflow-y-auto">
            <button data-testid="checkout-close" onClick={close} className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-border hover:border-volt hover:text-volt transition-colors duration-200"><X className="w-4 h-4" /></button>

            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-volt">Checkout</span>
            <h3 className="font-display font-bold text-2xl uppercase tracking-tight mt-2">{sel.label}</h3>
            <div className="font-display font-black text-4xl tracking-tighter mt-1">${sel.amount}</div>

            {!manual ? (
              <div className="mt-6 space-y-4">
                <input data-testid="checkout-name" className="w-full bg-background border border-border focus:border-volt outline-none px-3 py-2.5 text-sm transition-colors duration-200" placeholder="Your name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
                <input data-testid="checkout-contact" className="w-full bg-background border border-border focus:border-volt outline-none px-3 py-2.5 text-sm transition-colors duration-200" placeholder="Email or Discord to reach you" value={form.customer_contact} onChange={(e) => set("customer_contact", e.target.value)} />
                <textarea data-testid="checkout-notes" rows={3} className="w-full bg-background border border-border focus:border-volt outline-none px-3 py-2.5 text-sm transition-colors duration-200" placeholder="What do you want made? (details)" value={form.notes} onChange={(e) => set("notes", e.target.value)} />

                <div>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Payment method</span>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {[["stripe", "Card", CreditCard], ["cashapp", "CashApp", Link2], ["applepay", "Apple Pay", Apple]].map(([m, label, Icon]) => (
                      <button key={m} data-testid={`method-${m}`} onClick={() => setMethod(m)}
                        className={`flex flex-col items-center gap-1.5 py-3 border text-[10px] font-mono tracking-[0.1em] uppercase transition-colors duration-200 ${method === m ? "border-volt text-volt bg-volt/10" : "border-border text-muted-foreground hover:border-volt"}`}>
                        <Icon className="w-4 h-4" /> {label}
                      </button>
                    ))}
                  </div>
                </div>

                <button data-testid="checkout-submit" onClick={submit} disabled={busy}
                  className="w-full bg-volt text-white font-mono text-xs tracking-[0.2em] uppercase py-3.5 hover:brightness-110 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {method === "stripe" ? "Pay with card" : method === "cashapp" ? "Pay with CashApp" : "Pay with Apple Pay"}
                </button>
                {method !== "stripe" && <p className="text-[11px] text-muted-foreground text-center">Your order is logged as pending; Etoire confirms once payment lands.</p>}
              </div>
            ) : (
              <div data-testid="manual-instructions" className="mt-6 space-y-4">
                <div className="flex items-center gap-2 text-volt"><Check className="w-5 h-5" /><span className="font-mono text-xs tracking-[0.15em] uppercase">Order placed — now send payment</span></div>
                {manual.method === "cashapp" ? (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Send <b className="text-foreground">${manual.amount}</b> on CashApp to:</p>
                    <div className="flex items-center justify-between border border-border bg-background px-3 py-3">
                      <span className="font-display font-bold text-xl">{CASHAPP_TAG}</span>
                      <button onClick={() => copy(CASHAPP_TAG)} className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground hover:text-volt">Copy</button>
                    </div>
                    <a data-testid="cashapp-link" href={CASHAPP_URL} target="_blank" rel="noreferrer" className="w-full bg-volt text-white font-mono text-xs tracking-[0.2em] uppercase py-3.5 hover:brightness-110 transition-all duration-200 flex items-center justify-center gap-2">Open CashApp <ArrowUpRight className="w-4 h-4" /></a>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Send <b className="text-foreground">${manual.amount}</b> via Apple Pay / Apple Cash to:</p>
                    <div className="flex items-center justify-between border border-border bg-background px-3 py-3">
                      <span className="font-display font-bold text-lg break-all">{APPLEPAY_EMAIL}</span>
                      <button data-testid="applepay-copy" onClick={() => copy(APPLEPAY_EMAIL)} className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground hover:text-volt shrink-0 ml-2">Copy</button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Open the Messages or Wallet app on your Apple device, start an Apple Cash payment to this address, and include your name.</p>
                  </div>
                )}
                <button onClick={close} className="w-full border border-border font-mono text-[10px] tracking-[0.2em] uppercase py-3 hover:border-volt hover:text-volt transition-colors duration-200">Done</button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
